import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { X } from 'lucide-react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';

import api from '../../services/api';
import { formatDate, parseTableParams } from '../../utils/Utils';
import {
  getShiftRotation,
  updateShiftRotation,
  getShiftRotationUsers,
  assignShiftRotationUsers,
  batchRemoveShiftRotationUsers,
} from '../../services/shiftRotationService';
import { useAuth } from '../../context/AuthContext';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Tabs from '../../components/ui/Tabs';
import Button from '../../components/ui/Button';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import TextInput from '../../components/ui/TextInput';
import PersonCell from '../../components/ui/PersonCell';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import WorkHourPickerDrawer from '../shift-schedules/WorkHourPickerDrawer';

/* ───────── Styled Components ───────── */

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StickyHeader = styled.div`
  flex-shrink: 0;
`;

const TabContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-top: 8px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  margin-bottom: 24px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoLabel = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #A0A8B4;
`;

const InfoValue = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #004242;
`;

const CycleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  margin-bottom: 24px;
`;

const DayCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px 8px;
  border-radius: 6px;
  border: 1px solid ${({ $color }) => $color || '#E0E4E8'};
  background: ${({ $color }) => $color ? `${$color}26` : '#F5F7FA'};
  min-height: 72px;
  text-align: center;
  transition: outline 0.1s ease, box-shadow 0.1s ease;

  ${({ $clickable }) => $clickable && `
    cursor: pointer;
    &:hover { box-shadow: 0 2px 8px rgba(0, 0, 0, 0.10); }
  `}

  ${({ $preview }) => $preview && `
    outline: 2px dashed rgba(2, 145, 239, 0.5);
    outline-offset: 1px;
  `}

  ${({ $selected }) => $selected && `
    outline: 2px solid #0291EF;
    outline-offset: 1px;
  `}
`;

const CycleHint = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #7A8694;
  margin: -12px 0 16px 0;
`;

const CycleActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UnsavedNote = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #B45309;
`;

const PreviewWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PreviewTitle = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #004242;
`;

const PreviewStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
`;

const PreviewChip = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 6px 2px;
  border-radius: 4px;
  border: 1px solid ${({ $color }) => $color || '#E0E4E8'};
  background: ${({ $color }) => $color ? `${$color}26` : '#F5F7FA'};
  text-align: center;
`;

const PreviewDow = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 10px;
  color: #A0A8B4;
`;

const PreviewName = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #004242;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AssignBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 12px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  line-height: 1.5;
  ${({ $variant }) => ($variant === 'success' ? `
    background: rgba(2, 211, 191, 0.10);
    border: 1px solid rgba(2, 211, 191, 0.45);
    color: #00695C;
  ` : `
    background: rgba(255, 152, 0, 0.10);
    border: 1px solid rgba(255, 152, 0, 0.45);
    color: #B45309;
  `)}
`;

const AssignBannerText = styled.div`
  flex: 1;
`;

const AssignBannerClose = styled.button`
  display: flex;
  align-items: center;
  border: none;
  background: none;
  cursor: pointer;
  color: inherit;
  padding: 2px;
  opacity: 0.7;
  &:hover { opacity: 1; }
`;

const DayIndex = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #A0A8B4;
  margin-bottom: 4px;
`;

const DayName = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #004242;
`;

const DayCode = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  color: #A0A8B4;
`;

// Vardiyasız günün hafta tatili işareti (AKM POC §5.1).
const WeeklyRestTag = styled.span`
  margin-top: 4px;
  padding: 1px 6px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 10px;
  font-weight: 700;
  background: rgba(2, 145, 239, 0.1);
  color: #0270BA;
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #E8ECF0;
`;

const DrawerTitle = styled.h3`
  font-size: 16px;
  color: #004242;
  font-weight: 700;
  font-family: 'Nunito Sans', sans-serif;
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  background: none;
  color: #A0A8B4;
  cursor: pointer;
  &:hover { background: #F5F7FA; }
`;

const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  & > div:nth-child(1) { z-index: 104; }
  & > div:nth-child(2) { z-index: 103; }
  & > div:nth-child(3) { z-index: 102; }
  & > div:nth-child(4) { z-index: 101; }
  & > div:nth-child(5) { z-index: 100; }
`;

const DrawerFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #E8ECF0;
`;

const Container = styled.div``;

const HintText = styled.span`
  font-size: 12px;
  color: #7A8694;
  font-family: 'Nunito Sans', sans-serif;
  margin-top: -12px;
`;

/* ───────── Component ───────── */

export default function ShiftRotationDetailsPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const { canWrite } = useAuth();

  const [rotation, setRotation] = useState(null);
  const [activeTab, setActiveTab] = useState('cycle');

  // Döngü editörü: pendingDays null = düzenleme yok; {dayIndex: { workHourId|null, isWeeklyRest }}.
  // Hafta tatili yalnız vardiyasız günde anlamlıdır (AKM POC §5.1) — vardiyalı günde hep false.
  const [workHours, setWorkHours] = useState([]);
  const [pendingDays, setPendingDays] = useState(null);
  const [selAnchor, setSelAnchor] = useState(null);
  const [hoverDay, setHoverDay] = useState(null);
  const [selectedDays, setSelectedDays] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [cycleSaving, setCycleSaving] = useState(false);

  // Users DataTable state
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageIndex: 0, pageSize: 10 });
  const [usersLoading, setUsersLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Assign drawer state
  const [assignDrawerOpen, setAssignDrawerOpen] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedUserGroups, setSelectedUserGroups] = useState([]);
  const [offsetDays, setOffsetDays] = useState(1);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, userIds: [], name: '' });
  const [deleting, setDeleting] = useState(false);
  const [assignResult, setAssignResult] = useState(null);

  const fetchRotation = useCallback(async () => {
    try {
      const res = await getShiftRotation(id);
      setRotation(res.data?.data);
    } catch { /* interceptor */ }
  }, [id]);

  useEffect(() => {
    setUsersLoading(true);
    getShiftRotationUsers(id)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setUsers(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .catch(() => {})
      .finally(() => setUsersLoading(false));
  }, [id, queryParams, refreshTrigger]);

  useEffect(() => { fetchRotation(); }, [fetchRotation]);

  // Vardiya kataloğu: döngü editörünün seçici çekmecesi + ofset önizlemesi için
  useEffect(() => {
    let cancelled = false;
    api.get('/v1/work-hours', { params: { pageSize: 100 } })
      .then((res) => {
        if (cancelled) return;
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setWorkHours(items);
      })
      .catch(() => { if (!cancelled) setWorkHours([]); });
    return () => { cancelled = true; };
  }, []);

  const tabs = useMemo(() => [
    { key: 'cycle', label: t('shiftRotation.cycleDef') },
    { key: 'users', label: t('shiftRotation.assignedUsers') },
  ], [t]);

  /* ───────── Users DataTable columns ───────── */

  const baseColumns = useMemo(() => [
    {
      name: 'fullName', label: t('employee.fullName'),
      options: {
        filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' },
        customBodyRender: (value, tableMeta) => {
          const row = users[tableMeta.rowIndex];
          const name = value || row?.userFullName || `${row?.firstName || ''} ${row?.lastName || ''}`.trim();
          return <PersonCell name={name} profilePicture={row?.profilePicture || row?.userProfilePicture} />;
        },
      },
    },
    {
      name: 'email', label: t('employee.email'),
      options: {
        filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' },
        customBodyRender: (value, tableMeta) => {
          const row = users[tableMeta.rowIndex];
          return value || row?.userEmail || '-';
        },
      },
    },
    {
      name: 'offsetDays', label: t('shiftRotation.offsetDays'),
      options: {
        filter: false, sort: true,
        customBodyRender: (value) => value ?? '-',
      },
    },
  ], [t, users]);

  const handleRemoveClick = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, userIds: [row.userId || row.id], name: row.fullName || row.userFullName || row.email });
  }, []);

  // Tabloda çoklu satır seçimiyle toplu çıkarma (customIdSelector="userId")
  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, userIds: ids, name: t('shiftRotation.selectedUsersLabel', { count: ids.length }) });
  }, [t]);

  const handleRemoveConfirm = useCallback(async () => {
    setDeleting(true);
    try {
      await batchRemoveShiftRotationUsers(id, deleteConfirm.userIds);
      setRefreshTrigger((p) => p + 1);
    } catch { /* interceptor */ }
    setDeleteConfirm({ isOpen: false, userIds: [], name: '' });
    setDeleting(false);
  }, [id, deleteConfirm.userIds]);

  const actionOptions = useMemo(() => {
    if (!canWrite('SM')) return [];
    return [
      { type: 'delete', label: t('shiftRotation.removeUser'), onClick: handleRemoveClick },
    ];
  }, [t, canWrite, handleRemoveClick]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = users[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, users]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  /* ───────── Cycle Tab — Inline Editor ───────── */

  // Görüntülenecek günler: taslak (pendingDays) varsa o, yoksa sunucudaki hali.
  const effectiveDays = useMemo(() => {
    const cycleDays = rotation?.cycleDays || 0;
    const byIndex = new Map((rotation?.days || []).map((d) => [d.dayIndex, d]));
    return Array.from({ length: cycleDays }, (_, i) => {
      const base = byIndex.get(i);
      const pending = pendingDays ? pendingDays[i] : undefined;
      const workHourId = pendingDays ? (pending?.workHourId ?? null) : (base?.workHourId ?? null);
      const isWeeklyRest = !workHourId && (pendingDays ? !!pending?.isWeeklyRest : !!base?.isWeeklyRest);
      const wh = workHours.find((w) => w.id === workHourId);
      return {
        dayIndex: i,
        workHourId,
        isWeeklyRest,
        workHourName: wh?.name ?? (pendingDays ? null : base?.workHourName ?? null),
        workHourShortCode: wh?.shortCode ?? (pendingDays ? null : base?.workHourShortCode ?? null),
        workHourColorCode: wh?.colorCode ?? (pendingDays ? null : base?.workHourColorCode ?? null),
      };
    });
  }, [rotation, pendingDays, workHours]);

  // Otel takvimi etkileşimi: ilk tık başlangıç, ikinci tık bitiş; aynı güne
  // iki kez tıklamak tek günü seçer. Aralık tamamlanınca vardiya seçici çekmecesi açılır.
  const handleDayClick = useCallback((i) => {
    if (selAnchor === null) {
      setSelAnchor(i);
      setSelectedDays([i]);
      return;
    }
    const lo = Math.min(selAnchor, i);
    const hi = Math.max(selAnchor, i);
    const range = [];
    for (let d = lo; d <= hi; d++) range.push(d);
    setSelectedDays(range);
    setPickerOpen(true);
  }, [selAnchor]);

  const clearSelection = useCallback(() => {
    setPickerOpen(false);
    setSelectedDays([]);
    setSelAnchor(null);
    setHoverDay(null);
  }, []);

  const applyWorkHourToSelection = useCallback((workHourId, flags) => {
    const snapshot = {};
    effectiveDays.forEach((d) => {
      snapshot[d.dayIndex] = { workHourId: d.workHourId, isWeeklyRest: d.isWeeklyRest };
    });
    selectedDays.forEach((i) => {
      snapshot[i] = { workHourId, isWeeklyRest: !workHourId && !!flags?.isWeeklyRest };
    });
    setPendingDays(snapshot);
    clearSelection();
  }, [effectiveDays, selectedDays, clearSelection]);

  const handleCycleCancel = useCallback(() => {
    setPendingDays(null);
    clearSelection();
  }, [clearSelection]);

  const handleCycleSave = useCallback(async () => {
    if (!rotation || !pendingDays) return;
    setCycleSaving(true);
    try {
      await updateShiftRotation(id, {
        name: rotation.name,
        cycleDays: rotation.cycleDays,
        anchorDate: rotation.anchorDate,
        days: Object.entries(pendingDays).map(([dayIndex, day]) => ({
          dayIndex: Number(dayIndex),
          workHourId: day?.workHourId ?? null,
          isWeeklyRest: !day?.workHourId && !!day?.isWeeklyRest,
        })),
      });
      await fetchRotation();
      setPendingDays(null);
    } catch { /* interceptor */ }
    setCycleSaving(false);
  }, [id, rotation, pendingDays, fetchRotation]);

  // Aralık önizlemesi: ilk tık yapıldıysa fareyle üzerinden geçilen güne kadar
  const previewRange = useMemo(() => {
    if (selAnchor === null || hoverDay === null || pickerOpen) return new Set();
    const lo = Math.min(selAnchor, hoverDay);
    const hi = Math.max(selAnchor, hoverDay);
    const range = new Set();
    for (let d = lo; d <= hi; d++) range.add(d);
    return range;
  }, [selAnchor, hoverDay, pickerOpen]);

  /* ───────── Assign Users ───────── */

  const searchEmployees = useCallback(async (query) => {
    try {
      const params = query ? { 'firstName[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      return (res.data?.data?.data || []).map((e) => ({
        id: e.id,
        name: `${e.firstName || ''} ${e.lastName || ''}`.trim(),
        profilePicture: e.profilePicture,
      }));
    } catch { return []; }
  }, []);

  const searchDepartments = useCallback(async (query) => {
    try {
      const params = query ? { 'name[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/departments', { params });
      return (res.data?.data?.data || []).map((d) => ({ id: d.id, name: d.name }));
    } catch { return []; }
  }, []);

  const searchLocations = useCallback(async (query) => {
    try {
      const params = query ? { 'name[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/locations', { params });
      return (res.data?.data?.data || []).map((l) => ({ id: l.id, name: l.name }));
    } catch { return []; }
  }, []);

  const searchUserGroups = useCallback(async (query) => {
    try {
      const params = query ? { 'name[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/user-groups', { params });
      return (res.data?.data?.data || []).map((g) => ({ id: g.id, name: g.name }));
    } catch { return []; }
  }, []);

  const handleAssignClick = useCallback(() => {
    setSelectedEmployees([]);
    setSelectedDepartments([]);
    setSelectedLocations([]);
    setSelectedUserGroups([]);
    setOffsetDays(1);
    setAssignDrawerOpen(true);
  }, []);

  const handleAssignSave = useCallback(async () => {
    if (!selectedEmployees.length && !selectedDepartments.length && !selectedLocations.length && !selectedUserGroups.length) return;
    setAssignSaving(true);
    try {
      const res = await assignShiftRotationUsers(id, {
        userIds: selectedEmployees,
        departmentIds: selectedDepartments,
        locationIds: selectedLocations,
        userGroupIds: selectedUserGroups,
        offsetDays,
      });
      const d = res.data?.data || {};
      setAssignResult({
        assignedCount: d.assignedCount ?? 0,
        alreadyInThisRotation: d.alreadyInThisRotation ?? 0,
        skippedOtherRotation: d.skippedOtherRotation || [],
      });
      setRefreshTrigger((p) => p + 1);
      setAssignDrawerOpen(false);
    } catch { /* interceptor */ }
    setAssignSaving(false);
  }, [id, selectedEmployees, selectedDepartments, selectedLocations, selectedUserGroups, offsetDays]);

  /* ───────── Render ───────── */

  return (
    <PageContainer>
      <StickyHeader>
        <Breadcrumbs items={[
          { label: t('shiftRotation.title'), path: '/shift-rotations' },
          { label: rotation?.name || '...' },
        ]} />

        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
      </StickyHeader>

      <TabContent>
      {/* ─── Tab 1: Cycle Definition ─── */}
      {activeTab === 'cycle' && (
        <>
          <InfoRow>
            <InfoItem>
              <InfoLabel>{t('shiftRotation.cycleDays')}</InfoLabel>
              <InfoValue>{rotation?.cycleDays || '-'}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>{t('shiftRotation.anchorDate')}</InfoLabel>
              <InfoValue>{formatDate(rotation?.anchorDate, i18n.language)}</InfoValue>
            </InfoItem>
          </InfoRow>

          {canWrite('SM') && (
            <CycleHint>{t('shiftRotation.cycleEditHint')}</CycleHint>
          )}

          <CycleGrid>
            {effectiveDays.map((day) => {
              const isSelected = selectedDays.includes(day.dayIndex);
              const isPreview = !isSelected && previewRange.has(day.dayIndex);
              return (
                <DayCard
                  key={day.dayIndex}
                  $color={day.workHourColorCode || null}
                  $clickable={canWrite('SM')}
                  $selected={isSelected}
                  $preview={isPreview}
                  onClick={canWrite('SM') ? () => handleDayClick(day.dayIndex) : undefined}
                  onMouseEnter={canWrite('SM') ? () => setHoverDay(day.dayIndex) : undefined}
                >
                  <DayIndex>{t('shiftRotation.dayLabel', { index: day.dayIndex + 1 })}</DayIndex>
                  <DayName>{day.workHourName || 'OFF'}</DayName>
                  {day.workHourShortCode && <DayCode>{day.workHourShortCode}</DayCode>}
                  {day.isWeeklyRest && <WeeklyRestTag>{t('shiftRotation.weeklyRest')}</WeeklyRestTag>}
                </DayCard>
              );
            })}
          </CycleGrid>

          {pendingDays && (
            <CycleActions>
              <Button onClick={handleCycleSave} disabled={cycleSaving}>
                {cycleSaving ? t('common.loading') : t('common.save')}
              </Button>
              <Button variant="outline" onClick={handleCycleCancel} disabled={cycleSaving}>
                {t('common.cancel')}
              </Button>
              <UnsavedNote>{t('shiftRotation.unsavedChanges')}</UnsavedNote>
            </CycleActions>
          )}

          <WorkHourPickerDrawer
            isOpen={pickerOpen}
            onClose={clearSelection}
            onSelect={applyWorkHourToSelection}
            currentWorkHourId={selectedDays.length === 1 ? effectiveDays[selectedDays[0]]?.workHourId : null}
            subtitle={`${rotation?.name || ''} • ${t('shiftRotation.selectedDaysLabel', { count: selectedDays.length })}`}
            workHours={workHours}
            // Seçim döngüye BEKLEYEN değişiklik olarak yazılır (kalıcı kayıt sayfadaki Kaydet'le) —
            // çekmecenin düğmesi "Kaydet" değil "Seçili Günlere Uygula" der.
            submitLabel={t('shiftRotation.applyToSelectedDays')}
            flags={[{
              key: 'isWeeklyRest',
              label: t('shiftRotation.weeklyRest'),
              hint: t('shiftRotation.weeklyRestHint'),
              // Seçili günlerin HEPSİ zaten hafta tatiliyse işaretli başlar.
              initial: selectedDays.length > 0 && selectedDays.every((i) => effectiveDays[i]?.isWeeklyRest),
              offOnly: true,
            }]}
          />
        </>
      )}

      {/* ─── Tab 2: Assigned Users DataTable ─── */}
      {activeTab === 'users' && (
        <Container>
          <ConfirmDialog
            isOpen={deleteConfirm.isOpen}
            title={t('shiftRotation.removeUser')}
            message={t('shiftRotation.removeUserMessage', { name: deleteConfirm.name })}
            onConfirm={handleRemoveConfirm}
            onCancel={() => setDeleteConfirm({ isOpen: false, userIds: [], name: '' })}
            loading={deleting}
          />

          {assignResult && (
            <AssignBanner
              $variant={assignResult.assignedCount > 0 && assignResult.skippedOtherRotation.length === 0
                ? 'success'
                : 'warning'}
            >
              <AssignBannerText>
                {t('shiftRotation.assignResult.success', { count: assignResult.assignedCount })}
                {assignResult.alreadyInThisRotation > 0 && (
                  <>{' '}{t('shiftRotation.assignResult.alreadyInThisRotation', { count: assignResult.alreadyInThisRotation })}</>
                )}
                {assignResult.skippedOtherRotation.length > 0 && (
                  <>{' '}{t('shiftRotation.assignResult.skippedOtherRotation', {
                    names: assignResult.skippedOtherRotation
                      .map((s) => `${s.fullName} (${s.rotationName})`)
                      .join(', '),
                  })}</>
                )}
              </AssignBannerText>
              <AssignBannerClose onClick={() => setAssignResult(null)} aria-label={t('common.cancel')}>
                <X size={16} />
              </AssignBannerClose>
            </AssignBanner>
          )}

          <DataTable
            title={t('shiftRotation.assignedUsers')}
            fetching={usersLoading}
            columns={columns}
            data={users}
            totalRowCount={pagination.totalRowCount}
            pageIndex={pagination.pageIndex}
            pageSize={pagination.pageSize}
            canCreateNewRecord={canWrite('SM')}
            newButtonLabel={t('shiftRotation.assignUsers')}
            onNewButtonClick={handleAssignClick}
            customIdSelector="userId"
            onRowsDelete={canWrite('SM') ? handleRowsDelete : undefined}
            filterList={filterList}
            onFilterConfirm={handleFilterConfirm}
            onTableChanged={handleDataStateChange}
          />

          {/* Assign Users Drawer */}
          <Drawer
            open={assignDrawerOpen}
            onClose={() => setAssignDrawerOpen(false)}
            direction="right"
            size="45%"
            duration={150}
            overlayOpacity={0.2}
            style={{
              display: 'flex',
              flexDirection: 'column',
              maxWidth: '100vw',
              minWidth: 380,
              margin: '12px',
              borderRadius: '6px',
              height: 'calc(100vh - 24px)',
              boxShadow: '0 8px 32px rgba(0, 66, 66, 0.12)',
              zIndex: 1000,
            }}
          >
            <DrawerHeader>
              <DrawerTitle>{t('shiftRotation.assignUsers')}</DrawerTitle>
              <CloseBtn onClick={() => setAssignDrawerOpen(false)}><X size={20} /></CloseBtn>
            </DrawerHeader>

            <DrawerBody>
              <AsyncGenericSelect
                label={t('shiftRotation.assignByEmployee')}
                value={selectedEmployees}
                onChange={setSelectedEmployees}
                onSearch={searchEmployees}
                renderOption={(opt) => <PersonCell name={opt.name} profilePicture={opt.profilePicture} />}
                isMulti
              />

              <AsyncGenericSelect
                label={t('shiftRotation.assignByDepartment')}
                value={selectedDepartments}
                onChange={setSelectedDepartments}
                onSearch={searchDepartments}
                isMulti
              />

              <AsyncGenericSelect
                label={t('shiftRotation.assignByLocation')}
                value={selectedLocations}
                onChange={setSelectedLocations}
                onSearch={searchLocations}
                isMulti
              />

              <AsyncGenericSelect
                label={t('shiftRotation.assignByUserGroup')}
                value={selectedUserGroups}
                onChange={setSelectedUserGroups}
                onSearch={searchUserGroups}
                isMulti
              />

              <TextInput
                label={t('shiftRotation.offsetDays')}
                type="number"
                min={1}
                max={rotation?.cycleDays || undefined}
                value={offsetDays}
                onChange={(e) => {
                  const raw = Number(e.target.value) || 1;
                  const cycle = rotation?.cycleDays || 1;
                  const clamped = Math.min(Math.max(1, raw), cycle);
                  setOffsetDays(clamped);
                }}
              />
              <HintText>{t('shiftRotation.offsetDaysHint', { count: rotation?.cycleDays || 1 })}</HintText>

              {rotation && (rotation.days || []).length > 0 && (
                <PreviewWrap>
                  <PreviewTitle>{t('shiftRotation.offsetPreviewTitle')}</PreviewTitle>
                  <PreviewStrip>
                    {Array.from({ length: 7 }, (_, i) => {
                      const cycle = rotation.cycleDays || 1;
                      const dayIdx = ((offsetDays - 1) + i) % cycle;
                      const d = (rotation.days || []).find((x) => x.dayIndex === dayIdx);
                      const date = new Date(rotation.anchorDate);
                      date.setDate(date.getDate() + i);
                      return (
                        <PreviewChip key={i} $color={d?.workHourColorCode || null}>
                          <PreviewDow>
                            {date.toLocaleDateString(i18n.language, { weekday: 'short' })} {date.getDate()}
                          </PreviewDow>
                          <PreviewName>{d?.workHourShortCode || d?.workHourName || 'OFF'}</PreviewName>
                        </PreviewChip>
                      );
                    })}
                  </PreviewStrip>
                  <HintText>{t('shiftRotation.offsetPreviewHint')}</HintText>
                </PreviewWrap>
              )}
            </DrawerBody>

            <DrawerFooter>
              <Button variant="outline" onClick={() => setAssignDrawerOpen(false)} disabled={assignSaving}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleAssignSave} disabled={assignSaving}>
                {assignSaving ? t('common.loading') : t('common.save')}
              </Button>
            </DrawerFooter>
          </Drawer>
        </Container>
      )}
      </TabContent>
    </PageContainer>
  );
}
